import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Wallet, TrendingUp, TrendingDown, ArrowDownCircle, ArrowUpCircle, Activity, Copy, Shield, Zap } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatEUR = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0,00\u20AC';
  return value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '\u20AC';
};

export default function Portfolio() {
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [prevProfit, setPrevProfit] = useState(null);
  const [profitFlash, setProfitFlash] = useState(null); // "up" | "down" | null

  const fetchPortfolio = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const res = await axios.get(`${API}/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newData = res.data;
      
      // Detect profit change direction
      if (prevProfit !== null && newData.total_profit !== prevProfit) {
        const flash = newData.total_profit > prevProfit ? "up" : "down";
        setProfitFlash(flash);
        setTimeout(() => setProfitFlash(null), 2000);
      }
      setPrevProfit(newData.total_profit);
      setPortfolio(newData);
    } catch (e) {
      console.error("Error cargando portafolio:", e);
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error("Sesion expirada");
      }
    } finally {
      setLoading(false);
    }
  }, [prevProfit]);

  useEffect(() => {
    fetchPortfolio();
    const interval = setInterval(fetchPortfolio, 5000);
    return () => clearInterval(interval);
  }, [fetchPortfolio]);

  const generateChartData = useMemo(() => {
    const totalBalance = (portfolio?.balance || 0) + (portfolio?.total_profit || 0);
    if (totalBalance === 0) return [];
    
    const data = [];
    const now = new Date();
    const baseValue = totalBalance * 0.82;
    
    for (let i = 30; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      
      const trend = (30 - i) / 30;
      // Add realistic dips
      const noise = (Math.sin(i * 0.7) * 0.025) + (Math.sin(i * 1.8) * 0.01) + (Math.random() - 0.5) * 0.015;
      const value = baseValue + (totalBalance - baseValue) * trend + totalBalance * noise;
      
      data.push({
        date: date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        value: Math.max(0, value),
        fullDate: date.toISOString()
      });
    }
    
    if (data.length > 0) {
      data[data.length - 1].value = totalBalance;
    }
    
    return data;
  }, [portfolio]);

  useEffect(() => {
    setChartData(generateChartData);
  }, [generateChartData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FCD535] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalBalance = (portfolio?.balance || 0) + (portfolio?.total_profit || 0);
  const profitPercent = portfolio?.balance > 0 ? ((portfolio?.total_profit || 0) / portfolio.balance * 100) : 0;
  const isTrendDown = portfolio?.profit_trend === "down" || profitFlash === "down";
  const balanceColor = isTrendDown ? 'text-[#F6465D]' : 'text-[#0ECB81]';
  const balanceGlow = isTrendDown 
    ? 'drop-shadow-[0_0_10px_rgba(246,70,93,0.3)]' 
    : 'drop-shadow-[0_0_10px_rgba(14,203,129,0.2)]';

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#181A20] border border-[#2B3139] rounded-lg p-3 shadow-xl">
          <p className="text-[#848E9C] text-xs mb-0.5">{payload[0].payload.date}</p>
          <p className="text-[#0ECB81] font-mono font-bold text-lg">
            {payload[0].value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{'\u20AC'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-5" data-testid="portfolio-page">
      {/* Main Balance Card */}
      <Card className="card-dark overflow-hidden">
        <div className="relative bg-gradient-to-br from-[#0ECB81]/5 via-transparent to-[#FCD535]/5 p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <p className="text-[#848E9C] text-sm">Balance Total</p>
                {portfolio?.is_copying && (
                  <span className={`inline-flex items-center gap-1 ${isTrendDown ? 'bg-[#F6465D]/10 text-[#F6465D]' : 'bg-[#0ECB81]/10 text-[#0ECB81]'} text-xs font-semibold px-2 py-0.5 rounded-full transition-colors duration-300`}>
                    <span className={`w-1.5 h-1.5 ${isTrendDown ? 'bg-[#F6465D]' : 'bg-[#0ECB81]'} rounded-full animate-pulse`} />
                    {isTrendDown ? 'Perdida temporal' : 'Generando'}
                  </span>
                )}
              </div>
              <p className={`text-4xl lg:text-5xl font-bold font-mono ${balanceColor} ${balanceGlow} transition-colors duration-500 ${profitFlash === 'down' ? 'animate-pulse' : ''}`} data-testid="total-balance">
                {formatEUR(totalBalance)}
              </p>
              <div className="flex items-center gap-4 mt-1">
                <div>
                  <span className="text-[#848E9C] text-xs">Capital invertido: </span>
                  <span className="font-mono text-[#EAECEF] text-sm">{formatEUR(portfolio?.balance)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[#848E9C] text-xs">Ganancias: </span>
                  <span className={`font-mono text-sm font-semibold transition-colors duration-500 ${isTrendDown ? 'text-[#F6465D]' : 'text-[#0ECB81]'}`}>
                    {portfolio?.total_profit >= 0 ? '+' : ''}{formatEUR(portfolio?.total_profit)}
                  </span>
                  {profitPercent !== 0 && (
                    <span className={`text-xs font-mono ${isTrendDown ? 'text-[#F6465D]' : 'text-[#0ECB81]'}`}>({profitPercent.toFixed(1)}%)</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Link to="/deposit">
                <Button className="btn-success px-5 py-2.5 text-sm" data-testid="btn-deposit">
                  <ArrowDownCircle className="w-4 h-4 mr-2" />
                  Depositar
                </Button>
              </Link>
              <Link to="/withdraw">
                <Button className="bg-[#F6465D] hover:bg-[#F6465D]/90 text-white font-bold px-5 py-2.5 text-sm" data-testid="btn-withdraw">
                  <ArrowUpCircle className="w-4 h-4 mr-2" />
                  Retirar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </Card>

      {/* Balance Chart */}
      {totalBalance > 0 && (
        <Card className="card-dark">
          <CardHeader className="border-b border-[#2B3139] py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#EAECEF] text-base">Evolucion del Balance</CardTitle>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 bg-[#0ECB81] rounded-full"></span>
                <span className="text-xs text-[#848E9C]">Ultimos 30 dias</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ECB81" stopOpacity={0.25}/>
                      <stop offset="95%" stopColor="#0ECB81" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#848E9C', fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#848E9C', fontSize: 11 }}
                    tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}K` : `${value.toFixed(0)}`}
                    domain={['dataMin - 50', 'dataMax + 50']}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#0ECB81" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorValue)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 bg-[#FCD535]/10 rounded-lg flex items-center justify-center">
                <Wallet className="w-4 h-4 text-[#FCD535]" />
              </div>
            </div>
            <p className="text-xs text-[#848E9C] mb-0.5">Capital Invertido</p>
            <p className="text-xl font-bold font-mono text-[#EAECEF]" data-testid="invested-capital">
              {formatEUR(portfolio?.balance)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 bg-[#0ECB81]/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-[#0ECB81]" />
              </div>
            </div>
            <p className="text-xs text-[#848E9C] mb-0.5">Ganancias Totales</p>
            <p className="text-xl font-bold font-mono text-[#0ECB81]" data-testid="total-profit">
              +{formatEUR(portfolio?.total_profit)}
            </p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-9 h-9 bg-[#FCD535]/10 rounded-lg flex items-center justify-center">
                <Activity className="w-4 h-4 text-[#FCD535]" />
              </div>
            </div>
            <p className="text-xs text-[#848E9C] mb-0.5">Tasa de Ganancia</p>
            <p className="text-xl font-bold font-mono text-[#FCD535]" data-testid="profit-rate">
              {portfolio?.profit_rate?.toFixed(1)}%/h
            </p>
          </CardContent>
        </Card>
        
        <Card className="card-dark">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className={`w-9 h-9 ${portfolio?.is_copying ? 'bg-[#0ECB81]/10' : 'bg-[#2B3139]'} rounded-lg flex items-center justify-center`}>
                <Copy className={`w-4 h-4 ${portfolio?.is_copying ? 'text-[#0ECB81]' : 'text-[#848E9C]'}`} />
              </div>
            </div>
            <p className="text-xs text-[#848E9C] mb-0.5">Copy Trading</p>
            <p className={`text-xl font-bold ${portfolio?.is_copying ? 'text-[#0ECB81]' : 'text-[#848E9C]'}`} data-testid="copy-status">
              {portfolio?.is_copying ? 'Activo' : 'Inactivo'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Copy Trading CTA */}
      {!portfolio?.is_copying && portfolio?.balance > 0 && (
        <Card className="card-dark border-[#FCD535]/20 overflow-hidden">
          <div className="bg-gradient-to-r from-[#FCD535]/5 to-[#0ECB81]/5 p-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_15d8ac38-293d-45ed-a585-187f28b97866/artifacts/bpkxytwu_IMG_9072.jpeg"
                  alt="Andres Martin"
                  className="w-14 h-14 rounded-full object-cover object-top border-2 border-[#FCD535]"
                />
                <div>
                  <h3 className="text-base font-semibold text-[#EAECEF]">Activa Copy Trading</h3>
                  <p className="text-[#848E9C] text-sm">92,3% Win Rate - Tu dinero trabaja solo</p>
                </div>
              </div>
              <Link to="/copy-trading">
                <Button className="btn-primary" data-testid="start-copy-btn">
                  <Zap className="w-4 h-4 mr-2" />
                  Activar Ahora
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* No Balance */}
      {portfolio?.balance === 0 && (
        <Card className="card-dark border-[#FCD535]/20">
          <CardContent className="p-6 text-center">
            <Wallet className="w-12 h-12 text-[#FCD535] mx-auto mb-4 opacity-80" />
            <h3 className="text-lg font-semibold text-[#EAECEF] mb-2">Sin fondos disponibles</h3>
            <p className="text-[#848E9C] text-sm mb-4">Deposita fondos para empezar a generar ganancias automaticamente</p>
            <Link to="/deposit">
              <Button className="btn-primary" data-testid="deposit-cta">
                <ArrowDownCircle className="w-4 h-4 mr-2" />
                Depositar Ahora
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card className="card-dark">
        <CardHeader className="border-b border-[#2B3139] py-3 px-4">
          <CardTitle className="text-[#EAECEF] text-base">Actividad Reciente</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {portfolio?.trades?.length > 0 ? (
            <div className="divide-y divide-[#2B3139]/50">
              {portfolio.trades.map((trade, i) => (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-[#2B3139]/20 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${trade.profit_percent >= 0 ? 'bg-[#0ECB81]/10' : 'bg-[#F6465D]/10'}`}>
                      {trade.profit_percent >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-[#0ECB81]" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-[#F6465D]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#EAECEF] text-sm">{trade.crypto}</p>
                      <p className="text-xs text-[#848E9C]">{trade.type} - {formatEUR(trade.amount)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono text-sm font-semibold ${trade.profit_percent >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                      {trade.profit_percent >= 0 ? '+' : ''}{trade.profit_percent}%
                    </p>
                    <p className="text-xs text-[#848E9C]">{trade.status}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-[#848E9C]">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No hay operaciones recientes</p>
              <p className="text-xs mt-1">Activa Copy Trading para empezar a generar operaciones</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
